import { createRoot } from "react-dom/client";
import type { Container } from "react-dom/client";
import { notifyError } from "common/utils/app-monitoring/notify";
import { networkErrorReportOptions } from "common/utils/app-monitoring/network-error-fingerprint";

type ErrorKind = "caught" | "uncaught" | "recoverable";

const reportError =
  (kind: ErrorKind, appName: string) =>
  (error: unknown, errorInfo: { componentStack?: string | undefined }) => {
    console.error(`[${appName}] [React ${kind} error]`, error, errorInfo.componentStack);
    const err = error instanceof Error ? error : new Error(String(error));
    // MN-8955: split the escaped "Unable to connect to server" sentinel by its
    // networkSubType so the cluster no longer collapses into one HB bucket.
    const { fingerprint, context: networkContext } = networkErrorReportOptions(err);
    notifyError(err, {
      context: {
        reactErrorKind: kind,
        appName,
        componentStack: errorInfo.componentStack ?? null,
        ...networkContext
      },
      ...(fingerprint != null ? { fingerprint } : {})
    });
  };

export function createReactRoot(container: Container, appName: string) {
  return createRoot(container, {
    onCaughtError: reportError("caught", appName),
    onUncaughtError: reportError("uncaught", appName),
    onRecoverableError: reportError("recoverable", appName)
  });
}
